import React, { useState, useRef, useEffect } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import axios from "axios";
import { API_BASE_URL, LOCALSTORAGE_TOKEN_ITEM } from "constants/api";
import { DeviceSize } from "constants/deviceSize";
import UserStore from "stories/UserStore";
import { showToast } from "components/ui-kit/Toast";
import TrashIcon from "assets/trash.svg";
type ProfileSettingsModalProps = {
  open: boolean;
  onClose: () => void;
};

export const ProfileSettingsModal: React.FC<ProfileSettingsModalProps> = ({
  open,
  onClose,
}) => {
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [displayName, setDisplayName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [avatarFile, setAvatarFile] = useState<File | null>(null);
  const [countryId, setCountryId] = useState<number | null>(null);
  const [oldPassword, setOldPassword] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [newPasswordConfirm, setNewPasswordConfirm] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const emailInputRef = useRef<HTMLInputElement | null>(null);

  const applyProfileData = (data: any | null | undefined) => {
    if (!data) return;

    setFirstName(data.firstname ?? "");
    setLastName(data.lastname ?? "");
    setDisplayName(data.display_name ?? "");
    setPhone(data.phone_number ?? "");
    // Email теперь подставляем из профиля, чтобы поле "What's your email?" заполнялось автоматически
    setEmail(data.email ?? "");
    setAvatarUrl(data.avatar_url ?? null);
    setAvatarFile(null);
    setCountryId(
      typeof data.country_id === "number" && Number.isFinite(data.country_id)
        ? data.country_id
        : null
    );
  };

  const extractErrorMessage = (err: any, fallback: string) => {
    const responseData = err?.response?.data;
    console.error("[Profile] API error", {
      fallback,
      response: responseData,
    });

    if (typeof responseData?.message === "string" && responseData.message.trim()) {
      return responseData.message;
    }

    const errors = responseData?.errors;
    if (errors && typeof errors === "object") {
      const firstKey = Object.keys(errors)[0];
      const firstMessages =
        firstKey && Array.isArray((errors as any)[firstKey]) ? (errors as any)[firstKey] : null;
      const first = firstMessages && firstMessages[0];
      if (typeof first === "string" && first.trim()) {
        return first;
      }
    }

    return fallback;
  };

  useEffect(() => {
    if (!open) {
      setFirstName("");
      setLastName("");
      setDisplayName("");
      setPhone("");
      setEmail("");
      setAvatarUrl(null);
      setAvatarFile(null);
      setCountryId(null);
      setOldPassword("");
      setNewPassword("");
      setNewPasswordConfirm("");
      setError(null);
      return;
    }

    // 1) Мгновенно показать данные из кэша (UserStore / localStorage),
    //    чтобы не мигали пустые поля и дефолтный аватар
    const cachedUser = UserStore.user as any;
    if (cachedUser) {
      applyProfileData({
        id: cachedUser.id,
        email: cachedUser.email,
        firstname: cachedUser.firstname,
        lastname: cachedUser.lastname,
        display_name: cachedUser.display_name,
        phone_number: cachedUser.phone_number,
        avatar_url: cachedUser.avatar_url,
        country_id: (cachedUser as any).country_id,
      });
    } else {
      try {
        const raw = localStorage.getItem("slash.profile.cache");
        if (raw) {
          const parsed = JSON.parse(raw);
          if (parsed && typeof parsed === "object") {
            applyProfileData(parsed);
          }
        }
      } catch {
        // если localStorage недоступен или данные битые — просто игнорируем
      }
    }

    const fetchProfile = async () => {
      const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
      if (!token) {
        setError("You need to sign in again.");
        return;
      }

      try {
        setIsLoading(true);
        setError(null);
        const response = await axios.get(`${API_BASE_URL}/profile`, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        const data = response.data?.data;
        if (!data) return;

        applyProfileData(data);

        try {
          localStorage.setItem("slash.profile.cache", JSON.stringify(data));
        } catch {
          // не критично, если localStorage недоступен
        }

        // обновим глобальный UserStore, чтобы сайдбар показывал актуальные данные
        try {
          UserStore.setUser({
            ...(UserStore.user as any),
            id: data.id,
            email: data.email,
            firstname: data.firstname,
            lastname: data.lastname,
            display_name: data.display_name,
            phone_number: data.phone_number,
            avatar_url: data.avatar_url,
          });
          try {
            localStorage.setItem("slash.profile.cache", JSON.stringify(data));
          } catch {
            // ignore cache errors
          }
        } catch {
          // не критично, если типы не совпадут – можно пропустить
        }
      } catch (err: any) {
        const message = extractErrorMessage(err, "Failed to load profile.");
        setError(message);
        showToast(message, 3000, "error");
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [open]);

  if (!open) return null;

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange: React.ChangeEventHandler<HTMLInputElement> = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const url = URL.createObjectURL(file);
    setAvatarUrl((prev) => {
      if (prev) URL.revokeObjectURL(prev);
      return url;
    });
    setAvatarFile(file);
  };

  const handleRemoveAvatar = () => {
    const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
    if (!token) {
      const msg = "You need to sign in again.";
      setError(msg);
      showToast(msg, 3000, "error");
      return;
    }

    const doDelete = async () => {
      try {
        setIsSaving(true);
        setError(null);
        await axios.delete(`${API_BASE_URL}/profile/avatar`, {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        });

        setAvatarUrl((prev) => {
          if (prev && avatarFile) {
            URL.revokeObjectURL(prev);
          }
          return null;
        });
        setAvatarFile(null);

        try {
          UserStore.setUser({
            ...(UserStore.user as any),
            avatar_url: null,
          });
          try {
            const raw = localStorage.getItem("slash.profile.cache");
            if (raw) {
              const parsed = JSON.parse(raw);
              if (parsed && typeof parsed === "object") {
                parsed.avatar_url = null;
                localStorage.setItem("slash.profile.cache", JSON.stringify(parsed));
              }
            }
          } catch {
            // ignore cache errors
          }
        } catch {
          // ignore
        }

        showToast("Avatar removed.", 3000, "success");
      } catch (err: any) {
        const message = extractErrorMessage(
          err,
          "Failed to remove avatar. Please try again."
        );
        setError(message);
        showToast(message, 4000, "error");
      } finally {
        setIsSaving(false);
      }
    };

    void doDelete();
  };

  const handleSave = async () => {
    if (isSaving) return;

    const token = localStorage.getItem(LOCALSTORAGE_TOKEN_ITEM);
    if (!token) {
      const msg = "You need to sign in again.";
      setError(msg);
      showToast(msg, 3000, "error");
      return;
    }

    try {
      setIsSaving(true);
      setError(null);

      // 0) Если заполнялись поля пароля — сначала меняем пароль
      const isPasswordChangeRequested =
        oldPassword.trim().length > 0 ||
        newPassword.trim().length > 0 ||
        newPasswordConfirm.trim().length > 0;

      if (isPasswordChangeRequested) {
        if (!oldPassword || !newPassword || !newPasswordConfirm) {
          const msg = "Please fill in all password fields.";
          setError(msg);
          showToast(msg, 3000, "error");
          setIsSaving(false);
          return;
        }

        if (newPassword !== newPasswordConfirm) {
          const msg = "New password and confirmation do not match.";
          setError(msg);
          showToast(msg, 3000, "error");
          setIsSaving(false);
          return;
        }

        try {
          await axios.post(
            `${API_BASE_URL}/profile/change-password`,
            {
              old_password: oldPassword,
              new_password: newPassword,
              new_password_confirmation: newPasswordConfirm,
            },
            {
              headers: {
                Accept: "application/json",
                Authorization: `Bearer ${token}`,
              },
            }
          );

          showToast("Password changed successfully.", 3000, "success");
          setOldPassword("");
          setNewPassword("");
          setNewPasswordConfirm("");
        } catch (err: any) {
          const message = extractErrorMessage(
            err,
            "Failed to change password. Please check your data and try again."
          );
          setError(message);
          showToast(message, 4000, "error");
          setIsSaving(false);
          return;
        }
      }

      // 1) Обновление текстового профиля
      const updatePayload: any = {
        firstname: firstName || "",
        lastname: lastName || "",
        display_name: displayName || "",
        phone_number: phone || "",
        country_id: countryId ?? 0,
      };

      const updateResponse = await axios.put(
        `${API_BASE_URL}/profile`,
        updatePayload,
        {
          headers: {
            Accept: "application/json",
            Authorization: `Bearer ${token}`,
          },
        }
      );

      let latestData = updateResponse.data?.data;

      // 2) Загрузка аватара, если файл выбран
      if (avatarFile) {
        const formData = new FormData();
        formData.append("avatar", avatarFile);

        const avatarResponse = await axios.post(
          `${API_BASE_URL}/profile/avatar`,
          formData,
          {
            headers: {
              Accept: "application/json",
              Authorization: `Bearer ${token}`,
            },
          }
        );
        latestData = avatarResponse.data?.data || latestData;
      }

      if (latestData) {
        setAvatarUrl(latestData.avatar_url ?? avatarUrl);
        try {
          UserStore.setUser({
            ...(UserStore.user as any),
            id: latestData.id,
            email: latestData.email,
            firstname: latestData.firstname,
            lastname: latestData.lastname,
            display_name: latestData.display_name,
            phone_number: latestData.phone_number,
            avatar_url: latestData.avatar_url,
          });
          try {
            localStorage.setItem("slash.profile.cache", JSON.stringify(latestData));
          } catch {
            // ignore cache errors
          }
        } catch {
          // ignore
        }
      }

      showToast("Profile updated successfully.", 3000, "success");
      onClose();
    } catch (err: any) {
      const message = extractErrorMessage(
        err,
        "Failed to save profile. Please check your data and try again."
      );
      setError(message);
      showToast(message, 4000, "error");
    } finally {
      setIsSaving(false);
    }
  };

  const content = (
    <Overlay onClick={onClose}>
      <Dialog onClick={(e) => e.stopPropagation()}>
        {/* Hidden block to "catch" browser autofill so it doesn't touch visible fields */}
        <HiddenAutofillGroup>
          <input type="password" name="password" autoComplete="current-password" />
        </HiddenAutofillGroup>

        <HeaderRow>
          <Title>Profile information</Title>
          <ModalLogoutButton
            type="button"
            onClick={() => {
              (window as any).__isLoggingOut = true;
              localStorage.removeItem(LOCALSTORAGE_TOKEN_ITEM);
              UserStore.clearUser();
              window.location.href = "https://slashmusic.net/";
            }}
          >
            Logout
          </ModalLogoutButton>
        </HeaderRow>

        <TopRow>
          <AvatarColumn>
            <AvatarCircle>
              {avatarUrl ? (
                <AvatarImage src={avatarUrl} alt="Avatar preview" />
              ) : (
                <AvatarPlaceholder>🙂</AvatarPlaceholder>
              )}
            </AvatarCircle>
            <AvatarActions>
              <UploadButton type="button" onClick={handleUploadClick}>
                <UploadIcon />
                <span>Upload</span>
              </UploadButton>
              <RemoveButton type="button" onClick={handleRemoveAvatar}>
                <TrashIcon />
                <span>Delete</span>
              </RemoveButton>
            </AvatarActions>
            <HiddenFileInput
              ref={fileInputRef}
              type="file"
              accept="image/*"
              onChange={handleFileChange}
            />
          </AvatarColumn>

          <PasswordColumn>
            <PasswordSectionLabel>Change password</PasswordSectionLabel>
            <FieldBlock>
              <FieldLabel>Current password</FieldLabel>
              <FieldInput
                type="password"
            autoComplete="off"
            name="current-password-custom"
                value={oldPassword}
                onChange={(e) => setOldPassword(e.target.value)}
                placeholder="Enter current password"
              />
            </FieldBlock>
            <FieldBlock>
              <FieldLabel>New password</FieldLabel>
              <FieldInput
                type="password"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                placeholder="Enter new password"
              />
            </FieldBlock>
            <FieldBlock>
              <FieldLabel>Confirm new password</FieldLabel>
              <FieldInput
                type="password"
                value={newPasswordConfirm}
                onChange={(e) => setNewPasswordConfirm(e.target.value)}
                placeholder="Repeat new password"
              />
            </FieldBlock>
          </PasswordColumn>
        </TopRow>

        <FieldsGrid>
          <FieldBlock>
            <FieldLabel>First name</FieldLabel>
            <FieldInput
              value={firstName}
              onChange={(e) => setFirstName(e.target.value)}
              placeholder="Your first name"
            />
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>Last name</FieldLabel>
            <FieldInput
              value={lastName}
              onChange={(e) => setLastName(e.target.value)}
              placeholder="Your last name"
            />
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>Nickname</FieldLabel>
            <FieldInput
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="Nickname"
            />
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>Phone number</FieldLabel>
            <FieldInput
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="+1234567890"
            />
          </FieldBlock>
          <FieldBlock>
            <FieldLabel>What&apos;s your email?</FieldLabel>
            <FieldInput
              ref={emailInputRef}
              type="text"
              autoComplete="off"
              data-autocomplete="off"
              data-form-type="other"
              data-lpignore="true"
              name="profile-email-custom-no-autofill"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onFocus={(e) => {
                if (emailInputRef.current) {
                  emailInputRef.current.removeAttribute('readonly');
                }
              }}
              readOnly
              placeholder="email@example.com"
            />
          </FieldBlock>
        </FieldsGrid>

        <ActionsRow>
          <CancelButton type="button" onClick={onClose}>
            Cancel
          </CancelButton>
          {error && <ErrorText>{error}</ErrorText>}
          <SaveButton type="button" onClick={handleSave} disabled={isSaving}>
            {isSaving ? "Saving..." : "Save"}
          </SaveButton>
        </ActionsRow>
      </Dialog>
    </Overlay>
  );

  return createPortal(content, document.body);
};

const Overlay = styled.div`
  position: fixed;
  inset: 0;
  background: rgba(15, 23, 42, 0.45);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 10000;

  @media (max-width: ${DeviceSize.MobileMax}) {
    padding: 16px;
  }
`;

const Dialog = styled.div`
  width: 620px;
  max-width: 95vw;
  border-radius: 24px;
  background: #ffffff;
  padding: 24px 26px 22px 26px;
  box-shadow: 0 30px 80px rgba(15, 23, 42, 0.4);
  display: flex;
  flex-direction: column;
  gap: 18px;
  position: relative;

  @media (max-width: ${DeviceSize.MobileMax}) {
    width: 100%;
    max-width: 100%;
    max-height: 80vh;
    overflow-y: auto;
    -webkit-overflow-scrolling: touch;
    padding: 20px 18px 18px 18px;
  }
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
`;

const HiddenAutofillGroup = styled.div`
  position: absolute;
  opacity: 0;
  pointer-events: none;
  inset: 0;
  height: 0;
  width: 0;
  overflow: hidden;
`;

const Title = styled.div`
  font-size: 18px;
  font-weight: 600;
  color: #111827;
`;

const ModalLogoutButton = styled.button`
  border-radius: 999px;
  border: 1px solid rgba(248, 113, 113, 0.7);
  padding: 6px 14px;
  font-size: 13px;
  font-weight: 600;
  background: #ffffff;
  color: #b91c1c;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  box-shadow: 0 0 0 0 rgba(248, 113, 113, 0.0);
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;

  &:hover {
    background: #fee2e2;
    box-shadow: 0 4px 10px rgba(248, 113, 113, 0.35);
    transform: translateY(-0.5px);
  }
`;

const TopRow = styled.div`
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 24px;

  @media (max-width: ${DeviceSize.MobileMax}) {
    flex-direction: column;
    align-items: stretch;
  }
`;

const AvatarColumn = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
`;

const PasswordColumn = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 10px;
`;

const AvatarCircle = styled.div`
  width: 200px;
  height: 200px;
  border-radius: 999px;
  background: linear-gradient(135deg, #dbeafe 0%, #f5f3ff 50%, #fee2e2 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  padding: 3px;

  @media (max-width: ${DeviceSize.MobileMax}) {
    width: 140px;
    height: 140px;
  }
`;

const AvatarPlaceholder = styled.div`
  font-size: 40px;
`;

const AvatarImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 999px;
`;

const AvatarActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

const UploadButton = styled.button`
  border-radius: 999px;
  border: none;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 600;
  background: #111827;
  color: #ffffff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.15s ease, box-shadow 0.15s ease, transform 0.1s ease;

  &:hover {
    background: #020617;
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.35);
    transform: translateY(-0.5px);
  }
`;

const RemoveButton = styled.button`
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  padding: 6px 12px;
  font-size: 13px;
  font-weight: 500;
  background: #ffffff;
  color: #111827;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  transition: background 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
  }
`;

const UploadIcon = () => (
  <svg
    width="16"
    height="16"
    viewBox="0 0 24 24"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M12 3V15"
      stroke="#ffffff"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
    <path
      d="M7 8L12 3L17 8"
      stroke="#ffffff"
      strokeWidth="1.8"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M5 15V19C5 20.1046 5.89543 21 7 21H17C18.1046 21 19 20.1046 19 19V15"
      stroke="#ffffff"
      strokeWidth="1.8"
      strokeLinecap="round"
    />
  </svg>
);

const HiddenFileInput = styled.input`
  display: none;
`;

const FieldsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  margin-top: 6px;
`;

const PasswordSectionLabel = styled.div`
  margin-top: 6px;
  font-size: 13px;
  font-weight: 600;
  color: #111827;
`;

const FieldBlock = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const FieldLabel = styled.div`
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
`;

const FieldInput = styled.input`
  width: 100%;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  padding: 9px 14px;
  font-size: 14px;
  color: #111827;
  background: #f9fafb;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease, background 0.15s ease;

  &::placeholder {
    color: #9ca3af;
  }

  &:focus {
    border-color: #6777ff;
    box-shadow: 0 0 0 1px rgba(103, 119, 255, 0.35);
    background: #ffffff;
  }
`;

const ActionsRow = styled.div`
  margin-top: 10px;
  display: flex;
  justify-content: flex-end;
  align-items: center;
  gap: 10px;

  @media (max-width: ${DeviceSize.MobileMax}) {
    justify-content: space-between;
  }
`;

const SaveButton = styled.button`
  min-width: 120px;
  border-radius: 999px;
  border: none;
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 600;
  color: #ffffff;
  background: linear-gradient(90deg, #7987ff 0%, #ff7b7d 100%);
  cursor: pointer;
  box-shadow: 0 8px 18px rgba(248, 113, 113, 0.35);
  transition: transform 0.1s ease, box-shadow 0.15s ease;

  &:hover {
    transform: translateY(-0.5px);
    box-shadow: 0 10px 24px rgba(248, 113, 113, 0.45);
  }
`;

const CancelButton = styled.button`
  display: none;
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  padding: 9px 18px;
  font-size: 14px;
  font-weight: 500;
  background: #ffffff;
  color: #111827;
  cursor: pointer;

  @media (max-width: ${DeviceSize.MobileMax}) {
    display: inline-flex;
    align-items: center;
    justify-content: center;
  }
`;

const ChangePasswordButton = styled.button`
  border-radius: 999px;
  border: 1px solid #e5e7eb;
  padding: 9px 14px;
  font-size: 13px;
  font-weight: 500;
  background: #ffffff;
  color: #111827;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.15s ease, border-color 0.15s ease, box-shadow 0.15s ease;

  &:hover {
    background: #f9fafb;
    border-color: #d1d5db;
    box-shadow: 0 4px 10px rgba(15, 23, 42, 0.12);
  }
`;

const ErrorText = styled.div`
  font-size: 12px;
  color: #b91c1c;
  margin-right: 12px;
`;


